import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toNumber } from "@/lib/paging";
import { cn } from "@jbnl/shared-ui";

interface Props {
  /**
   * `string | number`, because a server-driven caller passes `PagedResult<T>`'s fields straight
   * through and `format: int32` reaches the client as a union. One coercion here, not per site.
   */
  page: number | string;
  pageSize: number | string;
  totalCount: number | string;
  onPageChange: (page: number) => void;
  /**
   * `full` draws numbered pages beside prev/next, for a page's own list. `compact` draws only
   * prev/next and the "pagina X van Y" text, for a list embedded in a dialog with less width to
   * give it and less reason to jump further than a page away.
   */
  variant?: "full" | "compact";
}

/**
 * The pager below a paginated list. Renders nothing once everything fits on one page - a table
 * of four rows gets the same generic list as one of a thousand, not a pager it never needs.
 */
export function Pager(props: Props) {
  const { onPageChange, variant = "full" } = props;
  const { t } = useTranslation("common");
  const page = toNumber(props.page);
  const pageSize = toNumber(props.pageSize);
  const totalCount = toNumber(props.totalCount);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (totalPages <= 1) return null;

  const canGoBack = page > 1;
  const canGoForward = page < totalPages;

  const vorigeVolgende = (
    <div className="flex items-center justify-center gap-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!canGoBack}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft />
        {t("actions.previous")}
      </Button>

      {variant === "compact" && (
        <span className={cn("text-sm text-muted-foreground")}>
          {t("table.pager.pageOfTotal", { page, total: totalPages })}
        </span>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!canGoForward}
        onClick={() => onPageChange(page + 1)}
      >
        {t("actions.next")}
        <ChevronRight />
      </Button>
    </div>
  );

  if (variant === "compact") {
    return (
      <nav aria-label={t("table.pager.label")} className="min-w-0">
        {vorigeVolgende}
      </nav>
    );
  }

  return (
    // The numbered pages get their own row, wrapping among themselves if the list is long
    // enough to need it, rather than sharing a row with Vorige/Volgende: a shared, wrapping row
    // could break between them, leaving Volgende stranded alone on a second line.
    <nav
      aria-label={t("table.pager.label")}
      className="flex min-w-0 flex-col items-center gap-2"
    >
      <ul className="flex flex-wrap items-center justify-center gap-1">
        {pageWindow(page, totalPages).map((entry, index) =>
          entry === "ellipsis" ? (
            <li
              key={`ellipsis-${index}`}
              className="px-1 text-muted-foreground"
            >
              <MoreHorizontal className="size-4" aria-hidden />
              <span className="sr-only">{t("table.pager.morePages")}</span>
            </li>
          ) : (
            <li key={entry}>
              <Button
                type="button"
                variant={entry === page ? "secondary" : "ghost"}
                size="sm"
                className="w-8 px-0"
                aria-current={entry === page ? "page" : undefined}
                aria-label={t("table.pager.gotoPage", { page: entry })}
                onClick={() => onPageChange(entry)}
              >
                {entry}
              </Button>
            </li>
          ),
        )}
      </ul>

      {vorigeVolgende}
    </nav>
  );
}

/**
 * First, last, the current page and its neighbours, and an ellipsis for whatever falls between -
 * so a thousand personen at twenty-five a page draws a handful of buttons, not forty of them.
 */
function pageWindow(page: number, totalPages: number): (number | "ellipsis")[] {
  const window = new Set([1, totalPages, page, page - 1, page + 1]);
  const numbers = [...window]
    .filter((n) => n >= 1 && n <= totalPages)
    .sort((a, b) => a - b);

  const result: (number | "ellipsis")[] = [];
  numbers.forEach((n, index) => {
    if (index > 0 && n - numbers[index - 1] > 1) {
      result.push("ellipsis");
    }
    result.push(n);
  });
  return result;
}
